import { createRootRoute, createRoute, createRouter, lazyRouteComponent, Navigate, Outlet, useParams } from "@tanstack/react-router";
import { KonseptSecici } from "./ekranlar/KonseptSecici";
import { KonseptA } from "./ekranlar/KonseptA";
import { KonseptB } from "./ekranlar/KonseptB";
import { Giris } from "./ekranlar/Giris";
import { SifremiUnuttum } from "./ekranlar/SifremiUnuttum";
import { ParolaBelirle } from "./ekranlar/ParolaBelirle";
import { useOturum } from "./lib/oturum";
import { YuklemePerdesi } from "./components/yukleme";
import { Kabuk } from "./Kabuk";
import { Pano } from "./ekranlar/Pano";
import { Islerim } from "./ekranlar/Islerim";
import { KomponentListe } from "./ekranlar/KomponentListe";
import { Admin } from "./ekranlar/Admin";
import { Mrb } from "./ekranlar/Mrb";
import { MrbDetay } from "./ekranlar/MrbDetay";
import { Teslim } from "./ekranlar/Teslim";
import { SokumForm, SokumListe } from "./ekranlar/Sokum";
import { Saha } from "./ekranlar/Saha";
import { Dogrulama } from "./ekranlar/Dogrulama";
import { DogrulamaRiser } from "./ekranlar/DogrulamaRiser";
import { DogrulamaEski } from "./ekranlar/DogrulamaEski";
import { Planlama } from "./ekranlar/Planlama";
import { Komponent } from "./ekranlar/Komponent";
import { Fitup } from "./ekranlar/Fitup";
import { Weldmap } from "./ekranlar/Weldmap";
import { Assembly } from "./ekranlar/Assembly";
import { Montaj } from "./ekranlar/Montaj";
import { RiserBelgeleri } from "./ekranlar/RiserBelgeleri";
import { ProjeBelgeleri } from "./ekranlar/ProjeBelgeleri";
import { Kullanicilar } from "./ekranlar/Kullanicilar";
import { Kutukler } from "./ekranlar/Kutukler";
import { Belgeler } from "./ekranlar/Belgeler";
import { cevir } from "@/lib/dil";
import { denemeAcik } from "@/lib/ortam";
import { goruntuleyiciAramasi } from "./lib/goruntuleyici";

/** Gerçek URL'ler: geri tuşu, yenileme ve paylaşılabilir bağlantı (erp router.tsx gerekçesi). */

/** Oturumsuz açılan yollar: davet/sıfırlama bağlantısına tıklayanın henüz hesabı yok. */
const HERKESE_ACIK = [/^\/sifremi-unuttum$/, /^\/parola-belirle\//];

function Kapi() {
  const { ben, yukleniyor } = useOturum();
  const yol = typeof window !== "undefined" ? window.location.pathname : "/";
  if (HERKESE_ACIK.some((r) => r.test(yol))) return <Outlet />;
  if (yukleniyor) return <YuklemePerdesi mesaj={cevir("Checking session")} />;
  if (!ben) return <Giris />;
  return <Outlet />;
}

const rootRoute = createRootRoute({ component: Kapi });

/** Uygulama kabuğu: melez A (sidebar + süreç rayı). Süreç ekranları bunun altında. */
const uygulama = createRoute({ getParentRoute: () => rootRoute, id: "uygulama", component: () => <Kabuk><Outlet /></Kabuk> });
const sayfa = <P extends string>(path: P, component: () => React.JSX.Element) => createRoute({ getParentRoute: () => uygulama, path, component });

/** D-071: açılış sayfası Dashboard; ilk teslimdeki /dashboard adresi de açılmaya devam eder. Eski açılış adresi köke yönlenir (K-9). */
const acilis = sayfa("/", Pano);
const pano = sayfa("/dashboard", Pano);
const eskiAcilis = sayfa("/komuta", () => <Navigate to="/" replace />);
const islerim = sayfa("/islerim", Islerim);
const admin = sayfa("/admin", Admin);
const mrb = sayfa("/mrb", Mrb);
const mrbDetay = createRoute({ getParentRoute: () => uygulama, path: "/mrb/$id", component: function M() { const { id } = useParams({ from: "/uygulama/mrb/$id" }); return <MrbDetay id={id} />; } });
const teslim = sayfa("/teslim", Teslim);
const sokum = sayfa("/sokum", SokumListe);
const sokumForm = createRoute({ getParentRoute: () => uygulama, path: "/sokum/$id", component: function S() { const { id } = useParams({ from: "/uygulama/sokum/$id" }); return <SokumForm id={id} />; } });
const saha = sayfa("/saha", Saha);
const dogrulama = sayfa("/dogrulama", Dogrulama);
const dogrulamaEski = sayfa("/dogrulama/eski", DogrulamaEski);
const dogrulamaRiser = createRoute({ getParentRoute: () => uygulama, path: "/dogrulama/$rn", component: function D() { const { rn } = useParams({ from: "/uygulama/dogrulama/$rn" }); return <DogrulamaRiser rn={rn} />; } });
const planlama = sayfa("/planlama", Planlama);
const komponentListe = sayfa("/komponent", () => <KomponentListe mod="hepsi" />);
const komponent = createRoute({ getParentRoute: () => uygulama, path: "/komponent/$id", component: function K() { const { id } = useParams({ from: "/uygulama/komponent/$id" }); return <Komponent id={id} />; } });
const fitupListe = sayfa("/fitup", () => <KomponentListe mod="fitup" />);
const fitup = createRoute({ getParentRoute: () => uygulama, path: "/fitup/$id", component: function F() { const { id } = useParams({ from: "/uygulama/fitup/$id" }); return <Fitup id={id} />; } });
/** D-037: kaynak başına fit-up formu. `key`: kaynak değişince form baştan kurulur (defaultValue'lu alanlar önceki kaynağın değerinde kalmasın). */
const fitupKaynak = createRoute({ getParentRoute: () => uygulama, path: "/fitup/$id/$weldId", component: function FK() { const { id, weldId } = useParams({ from: "/uygulama/fitup/$id/$weldId" }); return <Fitup key={weldId} id={id} weldId={weldId} />; } });
const weldmapListe = sayfa("/weldmap", () => <KomponentListe mod="weld" />);
const weldmap = createRoute({ getParentRoute: () => uygulama, path: "/weldmap/$id", component: function W() { const { id } = useParams({ from: "/uygulama/weldmap/$id" }); return <Weldmap id={id} />; } });
const assembly = sayfa("/assembly", Assembly);
/** D-068: riserın Assembly sayfası (Finishing'in alt sayfası; menü kalemi yok). `key`: riser değişince sayfa baştan kurulur. */
const montaj = createRoute({ getParentRoute: () => uygulama, path: "/assembly/$rn", component: function MS() { const { rn } = useParams({ from: "/uygulama/assembly/$rn" }); return <Montaj key={rn} rn={rn} />; } });
/** D-058: Riser Documentation. Riser seçilmeden yalnız seçici; `$rn` ile o riserin dosyası. */
const riserBelgeleri = sayfa("/riser-documentation", () => <RiserBelgeleri />);
const riserBelgeleriRiser = createRoute({ getParentRoute: () => uygulama, path: "/riser-documentation/$rn", component: function RB() { const { rn } = useParams({ from: "/uygulama/riser-documentation/$rn" }); return <RiserBelgeleri rn={rn} />; } });
/** D-059: Project documents. Üç sekme yerel durumdur; `$rn` ile "Riser" sekmesi o riserle açılır. */
const projeBelgeleri = sayfa("/project-documents", () => <ProjeBelgeleri />);
const projeBelgeleriRiser = createRoute({ getParentRoute: () => uygulama, path: "/project-documents/$rn", component: function PB() { const { rn } = useParams({ from: "/uygulama/project-documents/$rn" }); return <ProjeBelgeleri rn={rn} />; } });
const kullanicilar = sayfa("/kullanicilar", Kullanicilar);
const kutukler = sayfa("/kutukler", Kutukler);
/** D-074 (U-073): Documents: her yükleme tek sayfada; dosyalama bugünkü yollardan (Faz 1, okuma yok). */
const belgeler = sayfa("/belgeler", Belgeler);

/** Konsept sayfaları tasarım denemesidir: yalnız izinli hesap görür, diğerleri uygulamaya döner. */
function Konsept({ children }: { children: React.JSX.Element }) {
  const { ben } = useOturum();
  if (!ben?.konsept) return <Kabuk><Pano /></Kabuk>;
  return children;
}
const secici = createRoute({ getParentRoute: () => rootRoute, path: "/konsept", component: () => <Konsept><KonseptSecici /></Konsept> });
const a = createRoute({ getParentRoute: () => rootRoute, path: "/konsept/a", component: () => <Konsept><KonseptA /></Konsept> });
const b = createRoute({ getParentRoute: () => rootRoute, path: "/konsept/b", component: () => <Konsept><KonseptB /></Konsept> });
/** 3D saha denemesi: tam ekran, kabuksuz, navigasyonda yok; three.js ayrı pakette. Canlı alan adında rota kaydedilmez. */
const saha3d = createRoute({ getParentRoute: () => rootRoute, path: "/saha-3d", component: lazyRouteComponent(() => import("./ekranlar/Saha3d"), "Saha3d") });
/** D-074 Faz 2 (K37): belge görüntüleyici. Kabuksuz, menüde yok; Documents kartından ve sayfa bağlantılarından yeni sekmede açılır. Sayfa ve okunan yer adreste (lib/goruntuleyici); görme kuralı sunucuda (page-info, J1). */
const belgeGoruntuleyici = createRoute({ getParentRoute: () => rootRoute, path: "/belge/$id", validateSearch: goruntuleyiciAramasi, component: lazyRouteComponent(() => import("./ekranlar/BelgeGoruntuleyici"), "BelgeGoruntuleyici") });
const sifremiUnuttum = createRoute({ getParentRoute: () => rootRoute, path: "/sifremi-unuttum", component: SifremiUnuttum });
const parolaBelirle = createRoute({
  getParentRoute: () => rootRoute,
  path: "/parola-belirle/$jeton",
  component: function ParolaBelirleSayfa() {
    const { jeton } = useParams({ from: "/parola-belirle/$jeton" });
    return <ParolaBelirle jeton={jeton} />;
  },
});

export const router = createRouter({
  routeTree: rootRoute.addChildren([
    uygulama.addChildren([acilis, pano, eskiAcilis, islerim, admin, mrb, mrbDetay, teslim, sokum, sokumForm, saha, dogrulama, dogrulamaEski, dogrulamaRiser, planlama, komponentListe, komponent, fitupListe, fitup, fitupKaynak, weldmapListe, weldmap, assembly, montaj, riserBelgeleri, riserBelgeleriRiser, projeBelgeleri, projeBelgeleriRiser, kullanicilar, kutukler, belgeler]),
    secici, a, b, belgeGoruntuleyici, ...(denemeAcik() ? [saha3d] : []), sifremiUnuttum, parolaBelirle,
  ]),
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
